// Date utilities. We work with ISO YYYY-MM-DD strings server-side; only
// convert to Date for display rendering, never for storage.

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Parses YYYY-MM-DD into a date object at midnight UTC, avoiding the
// new Date('YYYY-MM-DD') gotcha which interprets as UTC then renders
// in local TZ -> off-by-one in negative TZ regions.
export function parseIsoDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

// "23 May" / "23 May 2026" if year differs from current
export function formatShortDate(s: string, includeYear = false): string {
  const d = parseIsoDate(s)
  const day = d.getUTCDate()
  const mon = MONTHS_SHORT[d.getUTCMonth()]
  if (includeYear) return `${day} ${mon} ${d.getUTCFullYear()}`
  return `${day} ${mon}`
}

// eachDate — yield every ISO yyyy-mm-dd string between startIso and endIso
// inclusive. Used by spec 15 ServiceEditor's "Date" picker to list every
// operating day. UTC arithmetic to dodge TZ off-by-ones.
export function eachDate(startIso: string, endIso: string): string[] {
  const out: string[] = []
  const start = parseIsoDate(startIso)
  const end = parseIsoDate(endIso)
  const cur = new Date(start.getTime())
  while (cur.getTime() <= end.getTime()) {
    out.push(cur.toISOString().slice(0, 10))
    cur.setUTCDate(cur.getUTCDate() + 1)
  }
  return out
}

// Format an event's day set (item 7). A single day shows as "23 May"; a
// contiguous run collapses to a range ("23 - 31 May"); a non-consecutive set
// lists up to three ("15 Jun, 20 Jul") or summarises ("15 Jun–20 Jul · 5 days").
export function formatEventDates(dates: string[] | null | undefined, startIso?: string, endIso?: string): string {
  if (!dates || dates.length === 0) {
    return startIso && endIso ? formatDateRange(startIso, endIso) : ''
  }
  const sorted = [...dates].sort()
  const first = sorted[0]!
  const last = sorted[sorted.length - 1]!
  if (sorted.length === 1) return formatShortDate(first)
  if (eachDate(first, last).length === sorted.length) return formatDateRange(first, last)
  if (sorted.length <= 3) return sorted.map((d) => formatShortDate(d)).join(', ')
  return `${formatShortDate(first)}–${formatShortDate(last)} · ${sorted.length} days`
}

// "23 - 31 May" or "23 May - 5 Jun" or "31 Dec - 6 Jan"
export function formatDateRange(startIso: string, endIso: string): string {
  if (startIso === endIso) return formatShortDate(startIso)
  const start = parseIsoDate(startIso)
  const end   = parseIsoDate(endIso)
  if (start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear()) {
    return `${start.getUTCDate()} - ${end.getUTCDate()} ${MONTHS_SHORT[end.getUTCMonth()]}`
  }
  return `${formatShortDate(startIso)} - ${formatShortDate(endIso)}`
}